import { useSelector } from 'react-redux';
import AccordionItemHandler from 'components/forms/new/AccordionItemHandler';
import SubAccordionItemHandler from 'components/forms/new/SubAccordionItemHandler';
import StatusCellRenderer from '../cellRenderer/StatusCellRenderer';
import NumbersAddressDropdown from '../formFields/NumbersAddressDropdown';
import NumbersAssignedPlansDropdown from '../formFields/NumbersAssignedPlansDropdown';
import OutboundCallerIDDropdown from '../formFields/OutboundCallerIDDropdown';
import NumberCountryDropdown from '../formFields/NumberCountryDropdown';
import CustomAttributes from '../formFields/CustomAttributes';
import { HStack, Box } from '@chakra-ui/react';
import walkthroughIds from '../../walkthroughIds';
import TRSRegistrationDropdown from '@/pages/telephony/numbers/edit/formFields/TRSRegistrationDropdown';
import ScopeType from 'utils/enums/ScopeType';

import useGetCurrentCompany from '@/queries/company/useGetCurrentCompany';
const EditNumberAccordion = ({
    errors,
    touched,
    value,
    editNumberData,
    previousEditNumberData,
    setEditNumberData,
    addresses,
    assignedPlans,
    outboundCallerIDs,
    trsRegistrations,
    registrationData,
    tableRowData,
}) => {
    const { darkMode } = useSelector((state) => state.settings);

    const { currentCompany } = useSelector((state) => {
        return { ...state.navigationLists };
    });
    const { data: currentCompanyData } = useGetCurrentCompany();
    // The user's own account level, not the org level they are currently
    // managing — a partner admin viewing a company sits at currentScope 20.
    const { requiredScope: accountScope } = useSelector((state) => state.login);

    // perNumberPlan: 0 = Disabled, 1 = Enabled, 2 = Partner Only — the latter
    // keeps the Plans dropdown available to partner admins and above only.
    const perNumberPlan =
        currentCompanyData?.companyBillingSettings?.perNumberPlan;
    const showAssignedPlans =
        perNumberPlan === 1 ||
        (perNumberPlan === 2 && accountScope >= ScopeType.PARTNER);

    // (3096): hide TRS Registration when there's nothing to offer, unless
    // this number already has one assigned (keeps it visible/editable).
    const showTRSRegistration =
        trsRegistrations?.length > 0 || !!registrationData;

    const formFields = (errors, touched) => {
        const fields = [
            <NumbersAddressDropdown
                key="addresses"
                data={editNumberData}
                setCurrentNumber={setEditNumberData}
                errors={errors}
                touched={touched}
                darkMode={darkMode}
                addresses={addresses}
            />,
            showAssignedPlans && (
                <NumbersAssignedPlansDropdown
                    key="assignedPlans"
                    data={editNumberData}
                    setCurrentNumber={setEditNumberData}
                    errors={errors}
                    touched={touched}
                    darkMode={darkMode}
                    assignedPlans={assignedPlans}
                />
            ),
            <OutboundCallerIDDropdown
                key="outBoundCallerId"
                data={editNumberData}
                setCurrentNumber={setEditNumberData}
                errors={errors}
                touched={touched}
                darkMode={darkMode}
                outboundCallerIDs={outboundCallerIDs}
            />,
            (editNumberData?.countries?.length > 1 ||
                previousEditNumberData?.countryID === null) && (
                <NumberCountryDropdown
                    key="countryID"
                    data={editNumberData}
                    previousEditNumberData={previousEditNumberData}
                    setCurrentNumber={setEditNumberData}
                    errors={errors}
                    touched={touched}
                    darkMode={darkMode}
                />
            ),
            showTRSRegistration && (
                <TRSRegistrationDropdown
                    key="registrationID"
                    data={editNumberData}
                    setCurrentNumber={setEditNumberData}
                    errors={errors}
                    touched={touched}
                    currentCompany={currentCompany}
                    darkMode={darkMode}
                    trsRegistrations={trsRegistrations}
                    registrationData={registrationData}
                />
            ),
            <SubAccordionItemHandler
                index={5}
                isLastItem
                key="customAttributes"
                label="Attributes">
                {[1, 2, 3].map((number) => (
                    <CustomAttributes
                        key={`customAttributes${number}`}
                        errors={errors}
                        touched={touched}
                        darkMode={darkMode}
                        number={number}
                        walkthroughId={
                            walkthroughIds.editNumbersGeneral[
                                `ufCustomAttribute${number}`
                            ]
                        }
                    />
                ))}
            </SubAccordionItemHandler>,
        ];
        return fields;
    };

    let filteredFields = formFields(errors, touched);

    filteredFields = filteredFields.filter(Boolean);

    // For single platform case: compute disabled like platform table does, otherwise, keep the same logic
    const isSinglePlatformCase = tableRowData?.platformStatus !== undefined;
    const computedStatus =
        isSinglePlatformCase &&
        tableRowData?.platformStatus === 0 &&
        tableRowData?.trunkID == null
            ? 'disabled'
            : editNumberData?.status;

    const accordionAdornment = (
        <HStack paddingRight={10}>
            <Box width="110px">
                <StatusCellRenderer
                    value={computedStatus}
                    trunkName={tableRowData?.trunkName}
                    parent
                />
            </Box>
        </HStack>
    );
    return (
        <AccordionItemHandler
            index={0}
            key={editNumberData?.id}
            label="Number"
            errors={errors}
            touched={touched}
            values={value}
            noOrder
            accordionAdornment={accordionAdornment}>
            {filteredFields}
        </AccordionItemHandler>
    );
};

export default EditNumberAccordion;
